import './AddUserModal.scss';
import { useForm, useFormWatch } from '@form-ts/react';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAction } from 'src/hooks/useAction';
import { Button } from 'src/modules/common/components/Button';
import { ModalBody } from 'src/modules/common/components/ModalBody';
import { ModalFooter } from 'src/modules/common/components/ModalFooter';
import { ModalHeader } from 'src/modules/common/components/ModalHeader';
import { UserManagementFormData } from 'src/modules/common/types/UserManagementFormData';
import { UserRole } from 'src/modules/common/types/UserRole';
import { DropdownField } from 'src/modules/form/components/DropdownField';
import { TextField } from 'src/modules/form/components/TextField';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';
import { ensureNonNull } from 'src/modules/form/utils/transform';
import { USER_ADD } from 'src/modules/users/actions/UserActions';
import { USER_ADD_FORM_SCHEMA } from 'src/modules/users/constants/userAddFormSchema';
import { UserSnapshot } from 'src/modules/users/types/UserSnapshot';

type Props = {
  readonly allUserSnapshots: ReadonlyArray<UserSnapshot>;
  readonly onCancel: () => void;
};

export const AddUserModal = ({ allUserSnapshots, onCancel }: Props): React.ReactElement => {
  const intl = useIntl();

  const addUser = useAction(USER_ADD.request);

  const form = useForm<UserManagementFormData, FormError>('admin.users.add', {
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      jobTitle: '',
      role: UserRole.USER,
    },
    reinitialize: false,
  });

  const schema = USER_ADD_FORM_SCHEMA(
    allUserSnapshots
      .map((userSnapshot) => userSnapshot.user.email),
  );

  useFormValidator(form, schema);

  const isLoading = useFormWatch(form, form.submitting.get);
  const addUserSubmitted = useFormWatch(form, form.submitted.get);
  const addUserErrors = useFormWatch(form, form.errors.get);

  const onSubmit = (): void => addUser(form);

  useEffect(() => {
    if (addUserSubmitted && Object.entries(addUserErrors).length === 0) {
      onCancel();

      form.change(form.initialState);
    }
  }, [addUserErrors, addUserSubmitted, form, onCancel]);

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({ id: 'admin/users/add/title' })}
        label={intl.formatMessage({ id: 'admin/users/add/label' })}
        handleClose={onCancel}
      />

      <ModalBody>
        <div className="bp-add-user-modal">
          <div className="bp-add-user-modal__row">
            <TextField
              field={form.field.at('email')}
              label={intl.formatMessage({ id: 'admin/user/form/field/email' })}
              required={true}
              size="md"
              maxLength={50}
            />

            <TextField
              field={form.field.at('firstName')}
              label={intl.formatMessage({ id: 'admin/user/form/field/firstName' })}
              maxLength={50}
              description={null}
              required={true}
              size="md"
            />
          </div>

          <div className="bp-add-user-modal__row">
            <TextField
              field={form.field.at('lastName')}
              label={intl.formatMessage({ id: 'admin/user/form/field/lastName' })}
              maxLength={50}
              description={null}
              required={true}
              size="md"
            />

            <DropdownField
              field={form.field.at('role').transform(ensureNonNull())}
              renderOption={RenderItem}
              options={ROLE_LIST}
              renderSelectedOption={renderSelectedItem}
              size="md"
              label={intl.formatMessage({ id: 'admin/user/form/field/role' })}
            />
          </div>

          <div className="bp-add-user-modal__row">
            <TextField
              field={form.field.at('jobTitle')}
              label={intl.formatMessage({ id: 'admin/user/form/field/jobTitle' })}
              maxLength={50}
              description={null}
              size="md"
            />

            <div className="bp-add-user-modal__input"/>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          fullWidth={true}
          kind="secondary"
          autoFocus={true}
          onClick={onCancel}
        >
          <FormattedMessage id="admin/users/add/cancel"/>
        </Button>

        <Button
          fullWidth={true}
          kind="primary"
          onClick={onSubmit}
          loading={isLoading}
        >
          <FormattedMessage id="admin/users/add/submit"/>
        </Button>
      </ModalFooter>
    </>
  );
};

const ROLE_LIST = [
  UserRole.USER,
  UserRole.ADMIN,
];
const ROLE_NAMES = {
  [UserRole.USER]: <FormattedMessage id="common/role/user"/>,
  [UserRole.ADMIN]: <FormattedMessage id="common/role/admin"/>,
};

const renderSelectedItem = (role: UserRole): React.ReactElement => (
  <span>{ROLE_NAMES[role]}</span>
);
const RenderItem = (role: UserRole): React.ReactElement => (
  <span>{ROLE_NAMES[role]}</span>
);
